/* gp-flyover.js
 * Geroprotect fly-over showpiece: rocket, astronaut, and UFO.
 * Quiet, transform-only flights. Vanilla JS (ES2018). No dependencies.
 * Works with JS disabled: every object stays out of the DOM until needed.
 */
(function () {
  'use strict';

  var PRELOAD_LEAD_MS = 1500;
  var MOBILE_BREAKPOINT = 750;

  var cfg = null;
  var state = null;
  var booted = false;
  var DRY = false;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(boot);

  function boot() {
    if (booted) {
      return;
    }
    booted = true;

    var body = document.body;
    if (!body || body.getAttribute('data-gp-flyover') !== '1') {
      return;
    }

    var reducedMotion = false;
    try {
      reducedMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { /* ignore */ }

    var saveData = false;
    try {
      saveData = !!(navigator.connection && navigator.connection.saveData);
    } catch (e) { /* ignore */ }

    var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    if (reducedMotion || saveData || viewportWidth < 360) {
      return;
    }

    if (typeof body.animate !== 'function') {
      return;
    }

    cfg = parseConfig();
    if (!cfg.objects.length) {
      return;
    }

    state = {
      layer: null,
      objs: {},
      order: cfg.objects.map(function (o) { return o.name; }),
      idx: 0,
      flights: 0,
      flying: false,
      preloading: false,
      timer: null,
      preloadTimer: null,
      disabled: false
    };

    var flag = readQueryFlag();
    if (flag === 'off') {
      state.disabled = true;
    }

    exposeApi();

    if (state.disabled) {
      return;
    }

    document.addEventListener('visibilitychange', onVisibilityChange, false);

    if (flag === 'now') {
      startNow();
    } else {
      arm(cfg.startDelay);
    }
  }

  function parseConfig() {
    var data = {};
    var el = document.getElementById('gp-flyover-config');
    if (el) {
      try {
        data = JSON.parse(el.textContent || '{}');
      } catch (e) {
        data = {};
      }
    }
    var objects = Array.isArray(data.objects) ? data.objects : [];
    return {
      objects: objects,
      startDelay: clamp(data.startDelay, 10000, 0),
      interval: clamp(data.interval, 30000, 1000),
      mobileInterval: clamp(data.mobileInterval, 45000, 1000),
      maxFlights: clamp(data.maxFlightsPerPage, 12, 1)
    };
  }

  function clamp(value, fallback, min) {
    var n = Number(value);
    if (!isFinite(n) || n < min) {
      return fallback;
    }
    return n;
  }

  function readQueryFlag() {
    var search = window.location.search || '';
    if (/(\?|&)gpfly=off(&|$)/.test(search)) {
      return 'off';
    }
    if (/(\?|&)gpfly=dry(&|$)/.test(search)) {
      DRY = true;
      return 'now';
    }
    if (/(\?|&)gpfly=now(&|$)/.test(search)) {
      return 'now';
    }
    return null;
  }

  function canPlayWebm() {
    try {
      var v = document.createElement('video');
      var result = v.canPlayType('video/webm; codecs="vp9"');
      return result === 'probably' || result === 'maybe';
    } catch (e) {
      return false;
    }
  }

  function findObject(name) {
    for (var i = 0; i < cfg.objects.length; i++) {
      if (cfg.objects[i] && cfg.objects[i].name === name) {
        return cfg.objects[i];
      }
    }
    return null;
  }

  function pickSource(obj) {
    if (canPlayWebm() && obj.webm) {
      return { src: obj.webm, type: 'video/webm; codecs="vp9"' };
    }
    if (obj.mov) {
      return { src: obj.mov, type: 'video/quicktime' };
    }
    if (obj.webm) {
      return { src: obj.webm, type: 'video/webm' };
    }
    return null;
  }

  function ensureObject(name) {
    if (state.objs[name]) {
      return state.objs[name];
    }

    var spec = findObject(name);
    if (!spec) {
      return null;
    }

    var chosen = pickSource(spec);
    if (!chosen) {
      return null;
    }

    if (!state.layer) {
      state.layer = document.createElement('div');
      state.layer.className = 'gp-flyover';
      state.layer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(state.layer);
    }

    var wrap = document.createElement('div');
    wrap.className = 'gp-flyover__obj gp-flyover__obj--' + name;
    wrap.setAttribute('aria-hidden', 'true');

    var inner = document.createElement('div');
    inner.className = 'gp-flyover__inner';

    var video = document.createElement('video');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('loop', '');
    video.setAttribute('preload', 'none');
    video.setAttribute('aria-hidden', 'true');
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    video.preload = 'none';

    var source = document.createElement('source');
    source.src = chosen.src;
    source.type = chosen.type;
    video.appendChild(source);

    inner.appendChild(video);
    wrap.appendChild(inner);
    state.layer.appendChild(wrap);

    var rec = {
      name: name,
      wrap: wrap,
      inner: inner,
      video: video,
      loaded: false,
      anim: null,
      innerAnim: null
    };

    state.objs[name] = rec;
    return rec;
  }

  function playVideo(video) {
    var p;
    try {
      p = video.play();
    } catch (e) {
      return Promise.reject(e);
    }
    if (p && typeof p.then === 'function') {
      return p;
    }
    return Promise.resolve();
  }

  function nextObject() {
    var name = state.order[state.idx % state.order.length];
    state.idx += 1;
    return name;
  }

  function intervalNow() {
    return window.innerWidth < MOBILE_BREAKPOINT ? cfg.mobileInterval : cfg.interval;
  }

  function blocked() {
    return state.disabled ||
      document.hidden ||
      state.flying ||
      state.preloading ||
      state.flights >= cfg.maxFlights;
  }

  function arm(ms) {
    clearTimeout(state.timer);
    state.timer = null;
    if (state.disabled || document.hidden || state.flights >= cfg.maxFlights) {
      return;
    }
    state.timer = setTimeout(tick, Math.max(0, ms));
  }

  function tick() {
    state.timer = null;
    if (blocked()) {
      return;
    }

    if (shouldSkip()) {
      arm(intervalNow());
      return;
    }

    var name = nextObject();
    var rec = ensureObject(name);
    if (!rec) {
      arm(intervalNow());
      return;
    }

    if (rec.loaded) {
      startFlight(rec);
    } else {
      state.preloading = true;
      try {
        rec.video.load();
      } catch (e) { /* ignore */ }
      rec.loaded = true;

      state.preloadTimer = setTimeout(function () {
        state.preloadTimer = null;
        state.preloading = false;

        if (state.disabled || document.hidden) {
          arm(0);
          return;
        }
        if (shouldSkip()) {
          arm(intervalNow());
          return;
        }
        startFlight(rec);
      }, PRELOAD_LEAD_MS);
    }
  }

  function shouldSkip() {
    if (document.body.classList.contains('overflow-hidden')) {
      return true;
    }

    var buybox = document.querySelector('[data-gp-buybox]');
    if (!buybox) {
      return false;
    }

    var rect = buybox.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return false;
    }

    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;

    var left = Math.max(rect.left, 0);
    var top = Math.max(rect.top, 0);
    var right = Math.min(rect.right, vw);
    var bottom = Math.min(rect.bottom, vh);

    var visibleW = right - left;
    var visibleH = bottom - top;
    if (visibleW <= 0 || visibleH <= 0) {
      return false;
    }

    var ratio = (visibleW * visibleH) / (rect.width * rect.height);
    return ratio >= 0.3;
  }

  var PLAY_TIMEOUT_MS = 6000;
  function startFlight(rec) {
    state.flying = true;
    rec.wrap.classList.add('is-flying');
    if (DRY) { rec.wrap.classList.add('is-dry'); }

    var settled = false;
    var timer = setTimeout(function () {
      if (settled) { return; }
      settled = true;
      // the video did not start in time (slow network): skip this flight quietly
      try { rec.video.pause(); } catch (e) { /* ignore */ }
      rec.wrap.classList.remove('is-flying');
      state.flying = false;
      arm(intervalNow());
    }, PLAY_TIMEOUT_MS);

    var started = DRY ? Promise.resolve() : playVideo(rec.video);
    started.then(function () {
      if (settled) { return; }
      settled = true;
      clearTimeout(timer);
      runAnimations(rec);
    }, function () {
      if (settled) { return; }
      settled = true;
      clearTimeout(timer);
      rec.wrap.classList.remove('is-flying');
      state.flying = false;
      state.flights += 1;
      arm(intervalNow());
    });
  }

  function runAnimations(rec) {
    if (rec.anim) {
      try { rec.anim.cancel(); } catch (e) { /* ignore */ }
      rec.anim = null;
    }

    var plan = flightPlan(rec);

    var anim = rec.wrap.animate(plan.keyframes, plan.options);
    rec.anim = anim;

    var innerAnim = rec.inner.animate(plan.innerKeyframes, plan.innerOptions);
    rec.innerAnim = innerAnim;

    anim.finished.then(function () {
      endFlight(rec);
    }, function () {
      // Cancelled (for example by stop()). Nothing to clean up here.
    });
  }

  function endFlight(rec) {
    if (rec.innerAnim) {
      try { rec.innerAnim.cancel(); } catch (e) { /* ignore */ }
      rec.innerAnim = null;
    }

    try { rec.video.pause(); } catch (e) { /* ignore */ }

    rec.wrap.classList.remove('is-flying');

    if (rec.anim) {
      try { rec.anim.cancel(); } catch (e) { /* ignore */ }
      rec.anim = null;
    }

    state.flying = false;
    state.flights += 1;
    arm(intervalNow());
  }

  function flightPlan(rec) {
    if (rec.name === 'rocket') {
      return {
        keyframes: [
          { offset: 0, transform: 'translate3d(calc(14vw - 50%), calc(112vh - 50%), 0) rotate(18deg)', easing: 'cubic-bezier(0.3, 0, 0.7, 0.4)' },
          { offset: 0.45, transform: 'translate3d(calc(34vw - 50%), calc(62vh - 50%), 0) rotate(20deg)', easing: 'cubic-bezier(0.5, 0, 0.9, 0.5)' },
          { offset: 1, transform: 'translate3d(calc(80vw - 50%), calc(-22vh - 50%), 0) rotate(22deg)' }
        ],
        options: { duration: 5200, fill: 'forwards' },
        innerKeyframes: [
          { transform: 'translate3d(-1.5px, 0, 0)' },
          { transform: 'translate3d(1.5px, 0, 0)' }
        ],
        innerOptions: { duration: 60, iterations: Infinity, direction: 'alternate', easing: 'linear' }
      };
    }

    if (rec.name === 'astronaut') {
      return {
        keyframes: [
          { transform: 'translate3d(calc(68vw - 50%), calc(-20vh - 50%), 0) rotate(-6deg)' },
          { transform: 'translate3d(calc(58vw - 50%), calc(112vh - 50%), 0) rotate(7deg)' }
        ],
        options: { duration: 20000, easing: 'linear', fill: 'forwards' },
        innerKeyframes: [
          { transform: 'translate3d(-26px, 0, 0)' },
          { transform: 'translate3d(26px, 0, 0)' }
        ],
        innerOptions: { duration: 6000, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' }
      };
    }

    // UFO: glide 4 s, hover 2 s, dart 1.8 s, then a short off-screen hold to reach 9 s.
    return {
      keyframes: [
        { offset: 0, transform: 'translate3d(calc(112vw - 50%), calc(24vh - 50%), 0) rotate(1.5deg)', easing: 'ease-out' },
        { offset: 0.40, transform: 'translate3d(calc(55vw - 50%), calc(22vh - 50%), 0) rotate(1.5deg)', easing: 'linear' },
        { offset: 0.72, transform: 'translate3d(calc(55vw - 50%), calc(22vh - 50%), 0) rotate(1.5deg)', easing: 'cubic-bezier(0.7, 0, 1, 0.6)' },
        { offset: 0.90, transform: 'translate3d(calc(-30vw - 50%), calc(22vh - 50%), 0) rotate(1.5deg)' },
        { offset: 1, transform: 'translate3d(calc(-30vw - 50%), calc(22vh - 50%), 0) rotate(1.5deg)' }
      ],
      options: { duration: 11000, fill: 'forwards' },
      innerKeyframes: [
        { transform: 'translate3d(0, -9px, 0)' },
        { transform: 'translate3d(0, 9px, 0)' }
      ],
      innerOptions: { duration: 1400, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' }
    };
  }

  function onVisibilityChange() {
    if (document.hidden) {
      clearTimeout(state.timer);
      state.timer = null;
      clearTimeout(state.preloadTimer);
      state.preloadTimer = null;
      if (state.preloading) {
        state.preloading = false;
      }
      return;
    }

    if (!state.disabled && !state.flying && !state.preloading && state.flights < cfg.maxFlights) {
      arm(0);
    }
  }

  function startNow() {
    var name = nextObject();
    var rec = ensureObject(name);
    if (!rec) {
      return;
    }
    if (!rec.loaded) {
      try { rec.video.load(); } catch (e) { /* ignore */ }
      rec.loaded = true;
    }

    setTimeout(function () {
      if (state.disabled || document.hidden || state.flying || state.preloading) {
        return;
      }
      startFlight(rec);
    }, 800);
  }

  function exposeApi() {
    window.GPFlyover = {
      trigger: function (name) {
        if (!state || state.disabled || state.flying || state.preloading) {
          return false;
        }
        var target = name || 'rocket';
        var rec = ensureObject(target);
        if (!rec) {
          return false;
        }
        if (!rec.loaded) {
          try { rec.video.load(); } catch (e) { /* ignore */ }
          rec.loaded = true;
        }
        startFlight(rec);
        return true;
      },
      stop: function () {
        if (!state) {
          return;
        }
        clearTimeout(state.timer);
        state.timer = null;
        clearTimeout(state.preloadTimer);
        state.preloadTimer = null;

        for (var key in state.objs) {
          if (Object.prototype.hasOwnProperty.call(state.objs, key)) {
            var rec = state.objs[key];
            if (rec.anim) {
              try { rec.anim.cancel(); } catch (e) { /* ignore */ }
              rec.anim = null;
            }
            if (rec.innerAnim) {
              try { rec.innerAnim.cancel(); } catch (e) { /* ignore */ }
              rec.innerAnim = null;
            }
            try { rec.video.pause(); } catch (e) { /* ignore */ }
            rec.wrap.classList.remove('is-flying');
          }
        }

        state.flying = false;
        state.preloading = false;
      },
      next: function () {
        if (!state || state.disabled || state.flying || state.preloading) {
          return false;
        }
        var name = nextObject();
        var rec = ensureObject(name);
        if (!rec) {
          return false;
        }
        if (!rec.loaded) {
          try { rec.video.load(); } catch (e) { /* ignore */ }
          rec.loaded = true;
        }
        startFlight(rec);
        return true;
      }
    };
  }
})();
